import { useQuery, useMutation } from '@tanstack/react-query'
import { REFERENCE_DATA_LIMIT, LOCATION_DATA_LIMIT } from '@/constants/api'
import { httpClient } from '@/configs/axios.config'
import type { components } from '@/types/api.generated'

// Types from generated API
type IndustrySearchDto = components['schemas']['IndustrySearchDto']
type IndustryListResponseDto = components['schemas']['IndustryListResponseDto']
type SectorSearchDto = components['schemas']['SectorSearchDto']
type SectorListResponseDto = components['schemas']['SectorListResponseDto']
type LocationSearchDto = components['schemas']['LocationSearchDto']
type LocationListResponseDto = components['schemas']['LocationListResponseDto']
type UserSearchDto = components['schemas']['UserSearchDto']
type UserListResponseDto = components['schemas']['UserListResponseDto']
type ContactSearchDto = components['schemas']['ContactSearchDto']
type ContactListResponseDto = components['schemas']['ContactListResponseDto']
type CompanySearchDto = components['schemas']['CompanySearchDto']
type CompanyListResponseDto = components['schemas']['CompanyListResponseDto']
type CountrySearchDto = components['schemas']['CountrySearchDto']
type CountryListResponseDto = components['schemas']['CountryListResponseDto']
type StateSearchDto = components['schemas']['StateSearchDto']
type StateListResponseDto = components['schemas']['StateListResponseDto']
type CitySearchDto = components['schemas']['CitySearchDto']
type CityListResponseDto = components['schemas']['CityListResponseDto']
type CreateLocationDto = components['schemas']['CreateLocationDto']
type LocationResponseDto = components['schemas']['LocationResponseDto']

export type Industry = components['schemas']['IndustryResponseDto']
export type Sector = components['schemas']['SectorResponseDto']
export type LocationItem = LocationListResponseDto['data'][number]
export type UserItem = UserListResponseDto['data'][number]
export type ContactItem = ContactListResponseDto['data'][number]
export type CompanyItem = CompanyListResponseDto['data'][number]
export type CountryItem = CountryListResponseDto['data'][number]
export type StateItem = StateListResponseDto['data'][number]
export type CityItem = CityListResponseDto['data'][number]

// =============================================================================
// Query Keys Factory
// =============================================================================

export const referenceDataKeys = {
  all: ['reference-data'] as const,
  industries: () => [...referenceDataKeys.all, 'industries'] as const,
  industriesSearch: (search: string) =>
    [...referenceDataKeys.industries(), search] as const,
  sectors: () => [...referenceDataKeys.all, 'sectors'] as const,
  sectorsSearch: (search: string) =>
    [...referenceDataKeys.sectors(), search] as const,
  locations: () => [...referenceDataKeys.all, 'locations'] as const,
  locationsSearch: (search: string) =>
    [...referenceDataKeys.locations(), search] as const,
  users: () => [...referenceDataKeys.all, 'users'] as const,
  usersSearch: (search: string) =>
    [...referenceDataKeys.users(), search] as const,
  contacts: () => [...referenceDataKeys.all, 'contacts'] as const,
  contactsSearch: (search: string) =>
    [...referenceDataKeys.contacts(), search] as const,
  companies: () => [...referenceDataKeys.all, 'companies'] as const,
  companiesSearch: (search: string) =>
    [...referenceDataKeys.companies(), search] as const,
  countries: () => [...referenceDataKeys.all, 'countries'] as const,
  countriesSearch: (search: string) =>
    [...referenceDataKeys.countries(), search] as const,
  states: () => [...referenceDataKeys.all, 'states'] as const,
  statesSearch: (countryId: string, search: string) =>
    [...referenceDataKeys.states(), countryId, search] as const,
  cities: () => [...referenceDataKeys.all, 'cities'] as const,
  citiesSearch: (countryId: string, stateId: string, search: string) =>
    [...referenceDataKeys.cities(), countryId, stateId, search] as const,
}

// =============================================================================
// Constants
// =============================================================================

const REFERENCE_DATA_STALE_TIME = 5 * 60 * 1000 // 5 minutes
const REFERENCE_DATA_GC_TIME = 10 * 60 * 1000 // 10 minutes

// =============================================================================
// API Functions
// =============================================================================

async function searchIndustries(
  params: IndustrySearchDto
): Promise<IndustryListResponseDto> {
  const response = await httpClient.post<IndustryListResponseDto>(
    '/industries/search',
    params
  )
  return response.data
}

async function searchSectors(
  params: SectorSearchDto
): Promise<SectorListResponseDto> {
  const response = await httpClient.post<SectorListResponseDto>(
    '/sectors/search',
    params
  )
  return response.data
}

async function searchLocations(
  params: LocationSearchDto
): Promise<LocationListResponseDto> {
  const response = await httpClient.post<LocationListResponseDto>(
    '/locations/search',
    params
  )
  return response.data
}

async function searchUsers(
  params: UserSearchDto
): Promise<UserListResponseDto> {
  const response = await httpClient.post<UserListResponseDto>(
    '/users/search',
    params
  )
  return response.data
}

async function searchContacts(
  params: ContactSearchDto
): Promise<ContactListResponseDto> {
  const response = await httpClient.post<ContactListResponseDto>(
    '/contacts/search',
    params
  )
  return response.data
}

async function searchCompaniesForDropdown(
  params: CompanySearchDto
): Promise<CompanyListResponseDto> {
  const response = await httpClient.post<CompanyListResponseDto>(
    '/companies/search',
    params
  )
  return response.data
}

async function searchCountries(
  params: CountrySearchDto
): Promise<CountryListResponseDto> {
  const response = await httpClient.post<CountryListResponseDto>(
    '/countries/search',
    params
  )
  return response.data
}

async function searchStates(
  params: StateSearchDto
): Promise<StateListResponseDto> {
  const response = await httpClient.post<StateListResponseDto>(
    '/states/search',
    params
  )
  return response.data
}

async function searchCities(
  params: CitySearchDto
): Promise<CityListResponseDto> {
  const response = await httpClient.post<CityListResponseDto>(
    '/cities/search',
    params
  )
  return response.data
}

async function getOrCreateLocation(
  params: CreateLocationDto
): Promise<LocationResponseDto> {
  const response = await httpClient.post<LocationResponseDto>(
    '/locations',
    params
  )
  return response.data
}

// =============================================================================
// Hooks
// =============================================================================

interface UseReferenceSearchOptions {
  search: string
  enabled?: boolean
}

/**
 * Hook to search industries
 * Only fetches when enabled (e.g., popover is open)
 */
export function useIndustriesSearch({
  search,
  enabled = true,
}: UseReferenceSearchOptions) {
  return useQuery({
    queryKey: referenceDataKeys.industriesSearch(search),
    queryFn: () =>
      searchIndustries({
        limit: REFERENCE_DATA_LIMIT,
        filters: search
          ? [
              {
                field: 'name',
                op: 'contains',
                value: search,
                logicalOp: 'AND',
              },
            ]
          : [],
        sorts: [{ field: 'name', direction: 'asc' }],
      }),
    enabled,
    staleTime: REFERENCE_DATA_STALE_TIME,
    gcTime: REFERENCE_DATA_GC_TIME,
  })
}

/**
 * Hook to search sectors
 * Only fetches when enabled (e.g., popover is open)
 */
export function useSectorsSearch({
  search,
  enabled = true,
}: UseReferenceSearchOptions) {
  return useQuery({
    queryKey: referenceDataKeys.sectorsSearch(search),
    queryFn: () =>
      searchSectors({
        limit: REFERENCE_DATA_LIMIT,
        filters: search
          ? [
              {
                field: 'name',
                op: 'contains',
                value: search,
                logicalOp: 'AND',
              },
            ]
          : [],
        sorts: [{ field: 'name', direction: 'asc' }],
      }),
    enabled,
    staleTime: REFERENCE_DATA_STALE_TIME,
    gcTime: REFERENCE_DATA_GC_TIME,
  })
}

/**
 * Hook to search locations
 * Only fetches when enabled (e.g., popover is open)
 * Note: Filtering is done client-side since displayName is computed from country/state/city
 */
export function useLocationsSearch({
  search,
  enabled = true,
}: UseReferenceSearchOptions) {
  return useQuery({
    queryKey: referenceDataKeys.locationsSearch(search),
    queryFn: () =>
      searchLocations({
        limit: LOCATION_DATA_LIMIT, // Fetch more since we filter client-side
        filters: [],
        sorts: [], // Let backend use default sort (createdAt desc)
      }),
    enabled,
    staleTime: REFERENCE_DATA_STALE_TIME,
    gcTime: REFERENCE_DATA_GC_TIME,
    select: (data) => {
      if (!search) return data
      const searchLower = search.toLowerCase()
      const filteredData = data.data.filter((location) => {
        const displayName = [
          location.city?.name,
          location.state?.name,
          location.country.name,
        ]
          .filter(Boolean)
          .join(', ')
        return displayName.toLowerCase().includes(searchLower)
      })
      return {
        ...data,
        data: filteredData,
      }
    },
  })
}

/**
 * Hook to search internal users (team members)
 * Only fetches when enabled (e.g., popover is open)
 */
export function useUsersSearch({
  search,
  enabled = true,
}: UseReferenceSearchOptions) {
  return useQuery({
    queryKey: referenceDataKeys.usersSearch(search),
    queryFn: () =>
      searchUsers({
        limit: REFERENCE_DATA_LIMIT,
        filters: search
          ? [
              {
                field: 'name',
                op: 'contains',
                value: search,
                logicalOp: 'AND',
              },
            ]
          : [],
        sorts: [{ field: 'name', direction: 'asc' }],
      }),
    enabled,
    staleTime: REFERENCE_DATA_STALE_TIME,
    gcTime: REFERENCE_DATA_GC_TIME,
  })
}

/**
 * Hook to search contacts
 * Only fetches when enabled (e.g., popover is open)
 */
export function useContactsSearch({
  search,
  enabled = true,
}: UseReferenceSearchOptions) {
  return useQuery({
    queryKey: referenceDataKeys.contactsSearch(search),
    queryFn: () =>
      searchContacts({
        limit: REFERENCE_DATA_LIMIT,
        filters: search
          ? [
              {
                field: 'name',
                op: 'contains',
                value: search,
                logicalOp: 'AND',
              },
            ]
          : [],
        sorts: [{ field: 'name', direction: 'asc' }],
      }),
    enabled,
    staleTime: REFERENCE_DATA_STALE_TIME,
    gcTime: REFERENCE_DATA_GC_TIME,
  })
}

/**
 * Hook to search companies for dropdowns
 * Only fetches when enabled (e.g., popover is open)
 */
export function useCompaniesDropdownSearch({
  search,
  enabled = true,
}: UseReferenceSearchOptions) {
  return useQuery({
    queryKey: referenceDataKeys.companiesSearch(search),
    queryFn: () =>
      searchCompaniesForDropdown({
        limit: REFERENCE_DATA_LIMIT,
        filters: search
          ? [
              {
                field: 'displayName',
                op: 'contains',
                value: search,
                logicalOp: 'AND',
              },
            ]
          : [],
        sorts: [{ field: 'displayName', direction: 'asc' }],
      }),
    enabled,
    staleTime: REFERENCE_DATA_STALE_TIME,
    gcTime: REFERENCE_DATA_GC_TIME,
  })
}

/**
 * Hook to search countries
 * Only fetches when enabled (e.g., popover is open)
 */
export function useCountriesSearch({
  search,
  enabled = true,
}: UseReferenceSearchOptions) {
  return useQuery({
    queryKey: referenceDataKeys.countriesSearch(search),
    queryFn: () =>
      searchCountries({
        limit: REFERENCE_DATA_LIMIT,
        filters: search
          ? [
              {
                field: 'name',
                op: 'contains',
                value: search,
                logicalOp: 'AND',
              },
            ]
          : [],
        sorts: [{ field: 'name', direction: 'asc' }],
      }),
    enabled,
    staleTime: REFERENCE_DATA_STALE_TIME,
    gcTime: REFERENCE_DATA_GC_TIME,
  })
}

interface UseStatesSearchOptions {
  countryId: string
  search: string
  enabled?: boolean
}

/**
 * Hook to search states filtered by country
 * Only fetches when enabled and countryId is provided
 */
export function useStatesSearch({
  countryId,
  search,
  enabled = true,
}: UseStatesSearchOptions) {
  return useQuery({
    queryKey: referenceDataKeys.statesSearch(countryId, search),
    queryFn: () =>
      searchStates({
        limit: REFERENCE_DATA_LIMIT,
        filters: [
          {
            field: 'countryId',
            op: 'eq',
            value: countryId,
            logicalOp: 'AND',
          },
          ...(search
            ? [
                {
                  field: 'name',
                  op: 'contains' as const,
                  value: search,
                  logicalOp: 'AND' as const,
                },
              ]
            : []),
        ],
        sorts: [{ field: 'name', direction: 'asc' }],
      }),
    enabled: enabled && !!countryId,
    staleTime: REFERENCE_DATA_STALE_TIME,
    gcTime: REFERENCE_DATA_GC_TIME,
  })
}

interface UseCitiesSearchOptions {
  countryId: string
  stateId?: string
  search: string
  enabled?: boolean
}

/**
 * Hook to search cities filtered by country and optionally state
 * Only fetches when enabled and countryId is provided
 */
export function useCitiesSearch({
  countryId,
  stateId,
  search,
  enabled = true,
}: UseCitiesSearchOptions) {
  return useQuery({
    queryKey: referenceDataKeys.citiesSearch(countryId, stateId ?? '', search),
    queryFn: () =>
      searchCities({
        limit: REFERENCE_DATA_LIMIT,
        filters: [
          {
            field: 'countryId',
            op: 'eq',
            value: countryId,
            logicalOp: 'AND',
          },
          ...(stateId
            ? [
                {
                  field: 'stateId',
                  op: 'eq' as const,
                  value: stateId,
                  logicalOp: 'AND' as const,
                },
              ]
            : []),
          ...(search
            ? [
                {
                  field: 'name',
                  op: 'contains' as const,
                  value: search,
                  logicalOp: 'AND' as const,
                },
              ]
            : []),
        ],
        sorts: [{ field: 'name', direction: 'asc' }],
      }),
    enabled: enabled && !!countryId,
    staleTime: REFERENCE_DATA_STALE_TIME,
    gcTime: REFERENCE_DATA_GC_TIME,
  })
}

/**
 * Mutation hook to get or create a location from country/state/city IDs
 */
export function useGetOrCreateLocation() {
  return useMutation({
    mutationFn: getOrCreateLocation,
  })
}
